import React, { useState } from 'react';
import {
  Grid,
  GridColumn,
  GridToolbar,
  GridDataStateChangeEvent,
  GridRowClickEvent
} from '@progress/kendo-react-grid';
import {
  process,
  State,
} from '@progress/kendo-data-query';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { Form, Field, FormElement } from '@progress/kendo-react-form';
import { FormNumericTextBox, FormTextArea } from '../FormComponents';
import { JobVariation } from '../../../data/interfaces';
import { useToast } from '../../../contexts/ToastContext';
import { createJobVariation, updateJobVariation, deleteJobVariation } from '../../../data/jobData';
import { FaExchangeAlt, FaFileSignature } from 'react-icons/fa';
import TabSectionHeader from './TabSectionHeader';

/** A quote converted to this job, as the job form loads it. */
interface JobQuoteRow {
  quoteId: number;
  ref: number;
  refFormatted: string;
  description?: string;
  cost?: number;
  wonDate?: string | null;
}

interface JobVariationsProps {
  variations: JobVariation[];
  /** Every quote converted to this job - its original and any added since. */
  quotes?: JobQuoteRow[];
  /** The quote the job was created from, tagged in the quotes table. */
  originalQuoteId?: number | null;
  jobId: number;
  lockEdit?: boolean;
  onVariationsChange?: (variations: JobVariation[]) => void;
}

interface VariationFormData {
  description: string;
  cost: number;
}

const JobVariations: React.FC<JobVariationsProps> = ({ 
  variations = [], 
  quotes = [],
  originalQuoteId = null,
  jobId, 
  lockEdit = false,
  onVariationsChange 
}) => {
  const { showToast } = useToast();
  
  const [dataState, setDataState] = useState<State>({
    take: 10,
    skip: 0
  });
  
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [currentVariation, setCurrentVariation] = useState<JobVariation | null>(null);
  const [isNewVariation, setIsNewVariation] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const handleFormSubmit = (values: { [name: string]: any }) => {
    // Normalize and capitalize first meaningful character of description
    let rawDescription: string = values.description || '';
    // Preserve leading whitespace except convert first non-whitespace char; requirement only mentions first letter
    // But typically we want to avoid accidental leading spaces, so trim start.
    rawDescription = rawDescription.trimStart();
    if (rawDescription.length > 0) {
      rawDescription = rawDescription.charAt(0).toUpperCase() + rawDescription.slice(1);
    }
    // Defensive numeric parsing in case the value comes through as a formatted string
    let rawCost = values.cost;
    // If the field failed to bind (common when custom format prevents proper value change), fall back to currentVariation cost on edit
    if ((rawCost === undefined || rawCost === null || rawCost === '') && !isNewVariation && currentVariation) {
      rawCost = currentVariation.cost;
    }
    let numericCost: number;
    if (typeof rawCost === 'number') {
      numericCost = rawCost;
    } else if (rawCost == null || rawCost === '') {
      numericCost = 0;
    } else {
      const parsed = parseFloat(String(rawCost).replace(/[^0-9.-]/g, ''));
      numericCost = isNaN(parsed) ? 0 : parsed;
    }
    console.log('Submitting variation form values:', values, 'Parsed cost:', numericCost, 'CurrentVariation cost fallback:', currentVariation?.cost);
    const formData: VariationFormData = {
      description: rawDescription,
      cost: numericCost
    };
    handleSaveVariation(formData);
  };
  const [isLoading, setIsLoading] = useState(false);

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
  };

  const processedData = process(variations, dataState);

  const handleAddVariation = () => {
    if (lockEdit) {
      showToast('Cannot add variations while edit is locked', 'error');
      return;
    }
    setCurrentVariation({
      jobId: jobId,
      description: '',
      cost: 0
    });
    setIsNewVariation(true);
    setFormKey(prev => prev + 1); // Force form re-render
    setEditDialogVisible(true);
  };

  const handleEditVariation = (variation: JobVariation) => {
    if (lockEdit) {
      showToast('Cannot edit variations while edit is locked', 'error');
      return;
    }
    console.log('Edit variation clicked:', variation);
    console.log('Variation description:', variation.description);
    console.log('Variation cost:', variation.cost);
    setCurrentVariation(variation);
    setIsNewVariation(false);
    setFormKey(prev => prev + 1); // Force form re-render with new data
    setEditDialogVisible(true);
  };

  const handleDeleteVariation = (variation: JobVariation) => {
    if (lockEdit) {
      showToast('Cannot delete variations while edit is locked', 'error');
      return;
    }
    setCurrentVariation(variation);
    setDeleteDialogVisible(true);
  };

  const handleSaveVariation = async (formData: VariationFormData) => {
    setIsLoading(true);
    try {
      let updatedVariations = [...variations];
      
      if (isNewVariation) {
        // Call API to create new variation
        const createdVariation = await createJobVariation({
          jobId: jobId,
          description: formData.description,
          cost: formData.cost
        });
        
        // Add the created variation with the ID returned from the API
        const newVariation: JobVariation = {
          ...createdVariation,
          jobId: jobId,
          description: formData.description,
          cost: formData.cost,
          createdDate: createdVariation.createdDate || new Date().toISOString()
        };
        updatedVariations.push(newVariation);
        showToast('Variation added successfully', 'success');
      } else if (currentVariation) {
        // Determine the correct ID to use for the variation
        const variationId = currentVariation.jobVariationId;
        
        if (!variationId) {
          throw new Error('Variation ID is missing - cannot update variation');
        }
        
        // Call API to update existing variation
        const updatedVariation = await updateJobVariation({
          id: variationId,
          jobId: jobId,
          description: formData.description,
          cost: formData.cost
        });
        console.log('Update response variation:', updatedVariation);
        
        const index = updatedVariations.findIndex(v => (v.jobVariationId && v.jobVariationId === variationId));
        if (index !== -1) {
          const existing = updatedVariations[index];
          const preservedCreatedDate = existing.createdDate || updatedVariation.createdDate || new Date().toISOString();
          updatedVariations[index] = {
            ...existing,                // keep any fields we don't explicitly overwrite
            ...updatedVariation,        // API returned fields (may or may not include createdDate)
            jobVariationId: variationId,
            jobId: jobId,
            description: formData.description,
            cost: formData.cost,
            createdDate: preservedCreatedDate, // ensure created date stays
            updatedDate: updatedVariation.updatedDate || new Date().toISOString()
          };
          console.log('Updated variation (final merged object):', updatedVariations[index]);
          showToast('Variation updated successfully', 'success');
        }
      }
      
      onVariationsChange?.(updatedVariations);
      setEditDialogVisible(false);
      setCurrentVariation(null);
    } catch (error) {
      console.error('Error saving variation:', error);
      showToast('Failed to save variation', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (currentVariation) {
      setIsLoading(true);
      try {
        // Determine the correct ID to use for the variation
        const variationId = currentVariation.jobVariationId;
        
        if (!variationId) {
          throw new Error('Variation ID is missing - cannot delete variation');
        }
        
        // Call API to delete the variation
        await deleteJobVariation(jobId, variationId);
        
        // Remove from local state
        const updatedVariations = variations.filter(v => 
          ((v.jobVariationId && v.jobVariationId === variationId))
        );
        onVariationsChange?.(updatedVariations);
        showToast('Variation deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting variation:', error);
        showToast('Failed to delete variation', 'error');
      } finally {
        setIsLoading(false);
      }
    }
    setDeleteDialogVisible(false);
    setCurrentVariation(null);
  };

  const handleRowClick = (event: GridRowClickEvent) => {
    handleEditVariation(event.dataItem);
  };

  const actionCell = (props: any) => (
    <td className="k-command-cell">
      <Button
        onClick={() => handleEditVariation(props.dataItem)}
        className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
        disabled={lockEdit}
      >
        Edit
      </Button>
      <Button
        onClick={() => handleDeleteVariation(props.dataItem)}
        className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-error"
        style={{ marginLeft: '8px' }}
        disabled={lockEdit}
      >
        Delete
      </Button>
    </td>
  );

  const currencyCell = (props: any) => (
    <td>
      £{props.dataItem.cost.toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}
    </td>
  );

  const friendlyDateCell = (props: any) => {
    const raw = props.dataItem.createdDate;
    let display = '';
    if (raw) {
      try {
        const d = new Date(raw);
        if (!isNaN(d.getTime())) {
          // e.g. 27 Aug 2025 14:32
          display = d.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          });
        } else {
          display = raw;
        }
      } catch {
        display = raw;
      }
    }
    return <td>{display}</td>;
  };

  const sortedQuotes = [...quotes].sort((a, b) => a.ref - b.ref);

  const formatGBP = (value: number) =>
    `£${value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const quoteValueCell = (props: any) => (
    <td style={{ textAlign: 'right' }}>{formatGBP(Number(props.dataItem.cost) || 0)}</td>
  );

  const quoteRefCell = (props: any) => (
    <td>
      {props.dataItem.refFormatted}
      {originalQuoteId && props.dataItem.quoteId === originalQuoteId && (
        <span className="jobquotes__tag">Original</span>
      )}
    </td>
  );

  const convertedDateCell = (props: any) => {
    const raw = props.dataItem.wonDate;
    const d = raw ? new Date(raw) : null;
    return (
      <td>
        {d && !isNaN(d.getTime())
          ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
          : ''}
      </td>
    );
  };

  // Opens in a new tab, like the quote chip at the top of the job.
  const quoteActionCell = (props: any) => (
    <td className="k-command-cell">
      <Button
        onClick={() => window.open(`/quotes/?id=${props.dataItem.quoteId}`, '_blank')}
        className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
      >
        View
      </Button>
    </td>
  );

  return (
    <div>
      <TabSectionHeader
        icon={<FaExchangeAlt />}
        title="Variations"
        description="Cost variations recorded against this job"
        actions={
          <button
            type="button"
            title="Add new variation"
            onClick={handleAddVariation}
            disabled={lockEdit}
            className="jobsection__action-btn"
          >
            Add Variation
          </button>
        }
      />
      <div style={{ padding: '0 1rem 1rem' }}>
        <Grid
          data={processedData}
          {...dataState}
          onDataStateChange={dataStateChange}
          onRowClick={handleRowClick}
          style={{ height: '400px' }}
        >
          <GridToolbar>
            <span style={{ color: '#666' }}>
              Total Variations: {variations.length} | 
              Total Value: £{variations.reduce((sum, v) => sum + v.cost, 0).toLocaleString('en-GB', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </span>
          </GridToolbar>
          <GridColumn field="description" title="Description" width="220px" />
          <GridColumn field="cost" title="Cost" width="100px" cell={currencyCell} />
          <GridColumn field="createdDate" title="Created" width="120px" cell={friendlyDateCell} />
          <GridColumn title="Actions" width="180px" cell={actionCell} />
        </Grid>
      </div>

      <TabSectionHeader
        icon={<FaFileSignature />}
        title="Additional Quotes Works"
        description="Quotes converted to this job, in quote reference order"
      />
      <div style={{ padding: '0 1rem 1rem' }}>
        {sortedQuotes.length === 0 ? (
          <p className="jobquotes__empty">No quotes have been converted to this job.</p>
        ) : (
          <Grid data={sortedQuotes}>
            <GridToolbar>
              <span style={{ color: '#666' }}>
                Total Quotes: {sortedQuotes.length} | 
                Total Value: {formatGBP(sortedQuotes.reduce((sum, q) => sum + (Number(q.cost) || 0), 0))}
              </span>
            </GridToolbar>
            <GridColumn field="refFormatted" title="Reference" width="160px" cell={quoteRefCell} />
            <GridColumn field="description" title="Description" />
            <GridColumn field="cost" title="Value" width="120px" cell={quoteValueCell} headerClassName="jobquotes__num" />
            <GridColumn field="wonDate" title="Date Converted" width="140px" cell={convertedDateCell} />
            <GridColumn title="Actions" width="110px" cell={quoteActionCell} />
          </Grid>
        )}
      </div>

      {/* Edit/Add Dialog */}
      {editDialogVisible && (
        <Dialog 
          title={isNewVariation ? "Add New Variation" : "Edit Variation"}
          onClose={() => setEditDialogVisible(false)}
          width="650px"
          className="variation-dialog"
        >
          <Form
            key={formKey}
            initialValues={{
              description: currentVariation?.description || '',
              cost: currentVariation?.cost || 0
            }}
            onSubmit={handleFormSubmit}
            render={(formRenderProps) => {
              console.log('Form initialValues:', {
                description: currentVariation?.description || '',
                cost: currentVariation?.cost || 0
              });
              console.log('Current variation in render:', currentVariation);
              return (
              <FormElement style={{ maxWidth: 650, margin: '0' }}>
                <div className="form__details__subcontainer">
                  <Field
                    name="description"
                    component={FormTextArea}
                    label="Description *"
                    required
                    rows={3}
                  />
                  <Field
                    name="cost"
                    component={FormNumericTextBox}
                    label="Cost (£) *"
                    required
                    // Use standard numeric format to avoid Kendo parsing issues with custom symbol; symbol shown in label
                    format="n2"
                    step={0.01}
                    // Remove min constraint to allow negative values
                  />
                </div>
                <DialogActionsBar>
                  <Button 
                    type="button" 
                    onClick={() => setEditDialogVisible(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    themeColor="primary"
                    disabled={!formRenderProps.allowSubmit || isLoading}
                  >
                    {isLoading ? 'Saving...' : (isNewVariation ? 'Add' : 'Update')}
                  </Button>
                </DialogActionsBar>
              </FormElement>
              )
            }}
          />
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialogVisible && (
        <Dialog 
          title="Confirm Delete"
          onClose={() => setDeleteDialogVisible(false)}
          width="400px"
        >
          <div style={{ padding: '1rem' }}>
            <p>Are you sure you want to delete this variation?</p>
            <p><strong>Description:</strong> {currentVariation?.description}</p>
            <p><strong>Cost:</strong> £{currentVariation?.cost.toLocaleString('en-GB', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}</p>
          </div>
          <DialogActionsBar>
            <Button 
              type="button" 
              onClick={() => setDeleteDialogVisible(false)}
            >
              Cancel
            </Button>
            <Button 
              type="button" 
              themeColor="error"
              onClick={handleConfirmDelete}
              disabled={isLoading}
            >
              {isLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

export default JobVariations;
